// src/authConfig.js
import { LogLevel, ProtocolMode } from "@azure/msal-browser";

const tenant   = process.env.REACT_APP_AAD_TENANT;      // e.g. "assertionai"
const clientId = process.env.REACT_APP_AAD_CLIENT_ID;   // SPA app GUID
const policy   = process.env.REACT_APP_AAD_SUSI_POLICY; // e.g. "signupsignin"

if (!tenant || !clientId || !policy) {
  console.warn("[AUTH] Missing env vars. TENANT:", tenant, "CLIENT_ID:", clientId, "POLICY:", policy);
}

const host = process.env.REACT_APP_AUTH_DOMAIN || `${tenant}.ciamlogin.com`;
const base = `https://${host}`;

// ✅ Only use this style for metadata (DO NOT use /tfp/)
export const metadataUrl =
  `${base}/${tenant}.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=${policy}`;

// Use custom domain if available, otherwise fall back to current origin
// This ensures redirects always go to the custom domain, not the Azure Static Web Apps default domain
const getRedirectUri = () => {
  const customDomain = process.env.REACT_APP_CUSTOM_DOMAIN || "studio.assertion-ai.com";
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const currentOrigin = window.location.origin;
  
  // In production, always use custom domain (never use Azure Static Web Apps default domain)
  if (process.env.NODE_ENV === "production" && !currentOrigin.includes("localhost")) {
    // If current origin is the Azure Static Web Apps domain, force custom domain
    if (currentOrigin.includes("azurestaticapps.net")) {
      return `${protocol}://${customDomain}`;
    }
    // If already on custom domain, use it
    if (currentOrigin.includes(customDomain)) {
      return `${protocol}://${customDomain}`;
    }
  }
  
  // Otherwise use current origin (for local dev or if already correct)
  return currentOrigin;
};

export const msalBaseConfig = {
  auth: {
    clientId,
    knownAuthorities: [host],
    redirectUri: getRedirectUri(),
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    protocolMode: ProtocolMode.OIDC,
    loggerOptions: {
      logLevel: LogLevel.Verbose,
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii) console.log(`[MSAL] ${message}`);
      },
    },
  },
};

export const loginRequest = {
  scopes: [
    "openid",
    "profile",
    "offline_access",
    `https://${tenant}.onmicrosoft.com/api/access_as_user`,
  ],
};

export const b2cPolicyName = policy;

// Every interactive sign-in names no account and sends no prompt, the way the in-page Sign in
// popup always has. Naming the cached account hinted its username, which in this CIAM tenant is the
// synthetic `<oid>@<tenant>.onmicrosoft.com` UPN, so Entra opened "Enter password" for an address
// the user has never seen (the tenant issues no login_hint claim, and prompt=login doesn't stop
// the hint). MSAL names the ACTIVE account on its own whenever one is cached, even on a bare
// loginRedirect, so the dead one is forgotten first: clearing it as active stops the hint at once,
// and dropping it from the cache (locally, no sign-out redirect) keeps boot from making it active
// again. A popup must open in the click's own turn or the browser blocks it, so popup callers
// don't await this.
export const cachedAccount = (instance) => instance.getActiveAccount() || instance.getAllAccounts()[0];

// Every cached account, not just the active one: with two cached, forgetting one left the other for
// boot to make active, and the next sign-in named it again.
export function forgetCachedAccount(instance) {
  const accounts = instance.getAllAccounts();
  instance.setActiveAccount(null);
  return Promise.all(accounts.map((account) => instance.clearCache({ account }).catch(() => {})));
}

// The address to show someone as "signed in as": the email claim, never the synthetic
// `<oid>@<tenant>.onmicrosoft.com` UPN that `username` holds for federated users.
export function accountEmail(account) {
  const c = account?.idTokenClaims || {};
  const email = c.email || (Array.isArray(c.emails) && c.emails[0]) || "";
  if (email) return email;
  const u = account?.username || "";
  return /@[^@]+\.onmicrosoft\.com$/i.test(u) ? "" : u;
}

// Interactive sign-in from an in-page "Sign in" button: a full-page redirect that comes back to the
// page it started on. Not a popup: Google's sign-in pages send an enforced
// Cross-Origin-Opener-Policy, which cuts the popup off from the page that opened it — so after
// "Continue with Google" the page never received the result, and the popup went on to render all
// of Studio inside itself.
export async function signInWithRedirect(instance) {
  const { savePostAuthRedirect } = await import("./utils/authRedirect");
  savePostAuthRedirect();          // back to this exact page (path, query and hash)
  await forgetCachedAccount(instance);
  await instance.loginRedirect({ ...loginRequest, extraQueryParameters: { p: b2cPolicyName } });
}
